import { TaxVoucherCreationParams, TaxVoucherSummary } from '@pedix-workspace/utils';
import fileSaver from 'file-saver';

export type AfipServiceParams = {
  certificateKey: string;
  certificate: string;
  cuit: number;
};

export type GenerateKeyAndCsrParams = {
  cuit: string;
  companyName: string;
  commonName: string;
};
export type ArcaUnkownError = {
  details: {
    Code: string;
    Msg: string;
  };
};
export type ArcaVoucherDataError = {
  message: string;
  resultado: string;
  observaciones: {
    code: string;
    message: string;
  }[];
};
export type ArcaDownloadVouchersArchiveParams = {
  apiBaseUrl: string;
  establishmentId: string;
  taxVoucherIds: string[];
  fileName: string;
};

export async function generateKeyAndCsr(apiBaseUrl: string, params: GenerateKeyAndCsrParams) {
  const generateKeyAndCsrUrl = `${apiBaseUrl}/arca/certificados/generate`;

  const response = await fetch(generateKeyAndCsrUrl, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    return Promise.reject(response.json());
  }

  return response
    .json()
    .then(data => <{ keyBase64: string; csrBase64: string }>data)
    .then(data => ({
      key: atob(data.keyBase64),
      csr: atob(data.csrBase64),
    }));
}

export async function validateKeyAndCert(
  apiBaseUrl: string,
  params: { certificateKey: string; certificate: string },
) {
  const validateKeyAndCertUrl = `${apiBaseUrl}/arca/certificados/validate`;

  return fetch(validateKeyAndCertUrl, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(async response => {
    const data = await response.json();

    if (!response.ok) {
      return Promise.reject(data);
    }

    return data;
  });
}

export async function getPointOfSales(apiBaseUrl: string, params: AfipServiceParams) {
  const getPointOfSalesUrl = `${apiBaseUrl}/arca/point-of-sales`;

  return fetch(getPointOfSalesUrl, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(async response => {
    const data = await response.json();

    if (!response.ok) {
      return Promise.reject(data);
    }

    return <{ pointOfSales: any[] }>data;
  });
}

export async function getParameters(apiBaseUrl: string, params: AfipServiceParams) {
  const getParametersUrl = `${apiBaseUrl}/arca/parameters`;

  return fetch(getParametersUrl, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(async response => {
    const data = await response.json();

    if (!response.ok) {
      return Promise.reject(data);
    }
    return data;
  });
}

export function getVoucherPdfUrl(
  apiBaseUrl: string,
  params: { establishmentId: string; taxVoucherId: string },
) {
  const getVoucherPdfUrl = `${apiBaseUrl}/arca/voucher-pdf/${params.establishmentId}/${params.taxVoucherId}`;

  return getVoucherPdfUrl;
}

export function getVoucherPdfArchiveUrl(apiBaseUrl: string, params: { establishmentId: string }) {
  const getVoucherPdfUrl = `${apiBaseUrl}/arca/voucher-pdf-archive/${params.establishmentId}`;

  return getVoucherPdfUrl;
}

export async function generateVoucher(apiBaseUrl: string, params: TaxVoucherCreationParams) {
  const generateVoucherUrl = `${apiBaseUrl}/arca/generate-bill`;

  return fetch(generateVoucherUrl, {
    method: 'POST',
    body: JSON.stringify(params),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(async response => {
    const data = await response.json();

    if (response.ok) {
      return <{ taxVoucherSummary: TaxVoucherSummary }>data;
    } else {
      return Promise.reject(data);
    }
  });
}
export async function generateCreditNote(
  apiBaseUrl: string,
  params: {
    establishmentId: string;
    taxVoucherId: string;
    reason?: string;
  },
) {
  const generateVoucherUrl = `${apiBaseUrl}/arca/generate-credit-note`;

  return await fetch(generateVoucherUrl, {
    method: 'POST',
    body: JSON.stringify(params),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(async response => {
    const data = await response.json();

    if (response.ok) {
      return <{ taxVoucherSummary: TaxVoucherSummary }>data;
    } else {
      return Promise.reject(data);
    }
  });
}

export async function downloadVouchersArchive({
  apiBaseUrl,
  establishmentId,
  taxVoucherIds,
  fileName,
}: ArcaDownloadVouchersArchiveParams): Promise<void> {
  const url = getVoucherPdfArchiveUrl(apiBaseUrl, {
    establishmentId,
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      taxVoucherIds,
      fileName,
    }),
  });

  if (!response.ok) {
    return Promise.reject(
      new Error(`HTTP error! status: ${response.status} - ${response.statusText}`),
    );
  }

  const blob = await response.blob();

  fileSaver.saveAs(blob, fileName);
}

export function isArcaUnkownError(error: any): error is ArcaUnkownError {
  if ('details' in error && error.details?.Code && error.details?.Msg) {
    return true;
  }

  return false;
}
export function isArcaVoucherDataError(error: any): error is ArcaVoucherDataError {
  if ('message' in error && 'resultado' in error && 'observaciones' in error) {
    return true;
  }

  return false;
}
export function isArcaGenericError(error: any): error is { message: string } {
  if ('message' in error) {
    return true;
  }

  return false;
}
